import { useState, useEffect, useRef, useContext, createContext } from "react";
import Api from "../apis/api";
import * as loginGatewayApi from "../apis/loginGatewayApi";
import _ from "lodash";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { LoadingSpinner } from "../utils";
import { toReifiedUser } from "./User";
import { useNavigate, useQuickLinkToken } from "../utils/hooks";
import config from "../config";
import posthog from "posthog-js";
import { prefetchVademecum, resetVademecumPromise, vademecumVariantFor } from "../modules/prescriptions/new/form/medicine/vademecum/hooks/vademecumPrefetch";
import { VADEMECUM } from "../modules/prescriptions/new/form/medicine/prescriptionMethods";
import { schedulePasswordExpiryReminder } from "../utils/passwordExpiry/passwordExpiryReminder";

const AuthContext = createContext();

// Clave por cuenta, no global -- si no, dos cuentas logueadas desde el mismo navegador se
// pisan el deviceToken una a la otra y login-gateway termina pidiendo OTP siempre a ambas.
const deviceTokenKey = (email) => `deviceToken:${email.trim().toLowerCase()}`;

const ignoreRequestErrors = (error) => {
  if (!axios.isAxiosError(error)) throw error;
};

export const AuthProvider = ({ children }) => {
  const [ user, setUser ] = useState();
  const [ isLoading, setIsLoading ] = useState(true);
  const [accessToken, setAccessToken] = useState();
  const navigate = useNavigate();
  const location = useLocation();
  // El email de la cuenta que está en medio del paso de OTP -- verifyCode no lo recibe como
  // parámetro (ver modules/login/index.js), así que hace falta guardarlo acá para saber en
  // qué clave escribir el deviceToken cuando la verificación termine.
  const pendingEmailRef = useRef();
  const quickLinkToken = useQuickLinkToken();

  const isUnprotectedRoute = location.pathname === "/login" || location.pathname === "/register";

  useEffect(() => {
    if (!_.isEmpty(user)) {
      const { pathname } = location;
      const redirectionPath = !isUnprotectedRoute ? pathname : user.landingRoute();

      return navigate(redirectionPath, {
        replace: true,
      });
    }

    const token = quickLinkToken || localStorage.getItem("accessToken");

    if (token)
      setLoggedInUser(token).catch(ignoreRequestErrors);
    else
      signout();
  }, [user]);

  const completeLogin = (accessToken, deviceToken, email) => {
    localStorage.setItem('accessToken', accessToken);
    if (deviceToken && email) localStorage.setItem(deviceTokenKey(email), deviceToken);
    schedulePasswordExpiryReminder();
    return setLoggedInUser(accessToken);
  };

  // Siempre pasa por login-gateway (nunca por api's /auth/login directamente).
  // Si el gateway responde "challenge_required" o "challenge_already_active" no se
  // completa el login todavía: se devuelve ese resultado para que el componente de
  // login pida el código (reusando el challengeId ya existente en el segundo caso).
  // "password_reset_required" es independiente del OTP: tampoco completa el login, pero
  // no hay segundo paso -- el componente de login solo muestra el aviso y no deja
  // avanzar hasta que el usuario cambie la contraseña desde afuera y vuelva a intentar.
  const signin = (email, password) => {
    const deviceToken = localStorage.getItem(deviceTokenKey(email));
    pendingEmailRef.current = email;
    return loginGatewayApi.login(email, password, deviceToken)
      .then(result => {
        if (loginGatewayApi.OTP_PENDING_STATUSES.includes(result?.status)) return result;
        if (result?.status === loginGatewayApi.PASSWORD_RESET_REQUIRED_STATUS) return result;
        return completeLogin(result.accessToken, result.deviceToken, email).then(() => result);
      });
  };

  const verifyCode = (challengeId, code) => loginGatewayApi.verify(challengeId, code)
    .then(result => completeLogin(result.accessToken, result.deviceToken, pendingEmailRef.current).then(() => result));

  const signout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem(config.healthInsurances.cache.key);
    setUser(null);
    setIsLoading(false);
    navigate(isUnprotectedRoute ? location.pathname : "/login", { replace: true });
    posthog.reset();
    resetVademecumPromise();
  };

  const signup = userToCreate => new Api().createUser(userToCreate);

  const setLoggedInUser = token => new Api(token).me()
    .then(toReifiedUser)
    .tap(setUser)
    .tap(() => setAccessToken(token))
    .tap(() => setIsLoading(false))
    .tap(user => posthog.identify(user.id, { email: user.email }))
    .tap(user => user.canPrescribeFrom(VADEMECUM) && prefetchVademecum(new Api(token), vademecumVariantFor(user)))
    .tapCatch(signout);

  const value = { signin, signout, user, signup, accessToken, verifyCode };

  return (
    <AuthContext.Provider value={value}>
      <LoadingSpinner isLoading={isLoading} absolute />
      {!isLoading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
